import { TCType } from '$lib/types/RoastLog';

// Base interface for coefficient arrays
interface BaseCoefficients {
  CEES: number[];
  DEES: number[];
  DEES2: number[];
  MAX_MV: number;
}

// Type-specific interfaces
interface JCoefficients extends BaseCoefficients {
  type: TCType.J;
}

interface KCoefficients extends BaseCoefficients {
  type: TCType.K;
  AAS: number[];
}

// Type guard to check if coefficients are for K-type
function isKCoefficients(coef: JCoefficients | KCoefficients): coef is KCoefficients {
  return coef.type === TCType.K;
}

// Abstract base class for thermocouples
abstract class ThermocoupleBase {
  protected coefficients: JCoefficients | KCoefficients;

  constructor(coefficients: JCoefficients | KCoefficients) {
    this.coefficients = coefficients;
  }

  protected baseTempTomV(temp: number): number {
    return this.coefficients.CEES.reduce(
      (accumulator, val, i) => accumulator + val * Math.pow(temp, i)
    );
  }

  mvToTemp(mv: number): number {
    const dees = mv <= this.coefficients.MAX_MV ? this.coefficients.DEES : this.coefficients.DEES2;
    return dees.reduce((accumulator, val, i) => accumulator + val * Math.pow(mv, i));
  }

  abstract tempTomV(temp: number): number;
}

// J-type thermocouple implementation
class JThermocouple extends ThermocoupleBase {
  constructor() {
    super({
      CEES: pow([
        [0.0],
        [0.50381187815, 10, -1],
        [0.3047583693, 10, -4],
        [-0.8568106572, 10, -7],
        [0.13228195295, 10, -9],
        [-0.17052958337, 10, -12],
        [0.20948090697, 10, -15],
        [-0.12538395336, 10, -18],
        [0.15631725697, 10, -22]
      ]),
      DEES: pow([
        [0.0],
        [1.978425, 10, 1],
        [-2.001204, 10, -1],
        [1.036969, 10, -2],
        [-2.549687, 10, -4],
        [3.585153, 10, -6],
        [-5.344285, 10, -8],
        [5.09989, 10, -10]
      ]),
      DEES2: pow([
        [-3.113581, 10, 3],
        [3.005436, 10, 2],
        [-9.947732, 10, 0],
        [1.702766, 10, -1],
        [-1.430334, 10, -3],
        [4.73886, 10, -6]
      ]),
      MAX_MV: 42.919,
      type: TCType.J
    });
  }

  tempTomV(temp: number): number {
    return this.baseTempTomV(temp);
  }
}

// K-type thermocouple implementation
class KThermocouple extends ThermocoupleBase {
  constructor() {
    super({
      AAS: pow([[0.1185976], [-0.1183432, 10, -3], [0.1269686, 10, 3]]),
      CEES: pow([
        [-0.017600413686],
        [0.038921204975],
        [0.18558770032, 10, -4],
        [-0.99457592874, 10, -7],
        [0.31840945719, 10, -9],
        [-0.56072844889, 10, -12],
        [0.56075059059, 10, -15],
        [-0.32020720003, 10, -18],
        [0.97151147152, 10, -22],
        [-0.12104721275, 10, -25]
      ]),
      DEES: pow([
        [0.0],
        [2.508355, 10, 1],
        [7.860106, 10, -2],
        [-2.503131, 10, -1],
        [8.31527, 10, -2],
        [-1.228034, 10, -2],
        [9.804036, 10, -4],
        [-4.41303, 10, -5],
        [1.057734, 10, -6],
        [-1.052755, 10, -8]
      ]),
      DEES2: pow([
        [-1.318058, 10, 2],
        [4.830222, 10, 1],
        [-1.646031, 10, 0],
        [5.464731, 10, -2],
        [-9.650715, 10, -4],
        [8.802193, 10, -6],
        [-3.11081, 10, -8]
      ]),
      MAX_MV: 20.644,
      type: TCType.K
    });
  }

  tempTomV(temp: number): number {
    if (!isKCoefficients(this.coefficients)) {
      throw new Error('Invalid coefficients for K-type thermocouple');
    }

    let mv = this.baseTempTomV(temp);
    mv +=
      this.coefficients.AAS[0] *
      Math.pow(Math.E, this.coefficients.AAS[1] * Math.pow(temp - this.coefficients.AAS[2], 2));

    return mv;
  }
}

// Factory function to create thermocouples
const createThermocouple = (type: TCType): ThermocoupleBase => {
  switch (type) {
    case 'J':
      return new JThermocouple();
    case 'K':
      return new KThermocouple();
    default:
      throw new Error(`Unsupported thermocouple type: ${type}`);
  }
};

export { createThermocouple, ThermocoupleBase };

// Helper function for coefficient calculations (kept from original)
function pow(measurements: [number, number?, number?][]): number[] {
  return measurements.map(([multiplicand, base, exponent]) =>
    base && exponent ? multiplicand * Math.pow(base, exponent) : multiplicand
  );
}
